import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { ldBreadcrumb, ldOrganization, ldWebsite } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";
import { Search, Clock, ArrowRight, BookOpen } from "lucide-react";

const PROFESSION_LABELS = {
  "avocat": "Avocat",
  "expert-comptable": "Expert-comptable",
  "notaire": "Notaire",
};

export default function Insights() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const profession = searchParams.get("profession") || "";

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (profession) params.profession = profession;
    apiClient.get("/insights", { params })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [q, profession]);

  const setProfession = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p) next.set("profession", p); else next.delete("profession");
    setSearchParams(next);
  };

  return (
    <div className="bg-[#FAFAF7] min-h-screen">
      <Seo
        title="Insights — Analyses et dossiers professionnels"
        description="Articles, analyses et guides institutionnels rédigés par les spécialistes référencés CONFILEX. Droit, fiscalité, comptabilité, patrimoine."
        type="website"
        canonical={typeof window !== "undefined" ? `${window.location.origin}/insights` : undefined}
        jsonLd={[
          ldOrganization(),
          ldWebsite(),
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Insights", url: "/insights" },
          ]),
        ]}
      />
      <Navbar variant="light" />

      {/* Hero */}
      <section className="bg-gradient-to-b from-white to-[#FAFAF7] border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-4">
            <BookOpen className="w-3.5 h-3.5" /> Cahier éditorial
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-[56px] tracking-tight text-[#0B3A2A] leading-[1.05] mb-5 max-w-3xl">
            Analyses, dossiers et <span className="italic text-[#C9A961]">repères</span> professionnels.
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed mb-8">
            Articles institutionnels rédigés par les spécialistes référencés et le comité éditorial CONFILEX. Aucune publicité, aucun classement — uniquement de la matière professionnelle.
          </p>

          {/* Search + filters */}
          <div className="flex flex-col md:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un article, un thème, un domaine…"
                data-testid="insights-search"
                className="h-12 pl-10 rounded-xl border-slate-200 bg-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <ProfChip active={profession === ""} onClick={() => setProfession("")} label="Tous les domaines" testid="prof-all" />
            {Object.entries(PROFESSION_LABELS).map(([k, label]) => (
              <ProfChip
                key={k}
                active={profession === k}
                onClick={() => setProfession(k)}
                label={label}
                testid={`prof-${k}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Articles grid */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="text-xs text-slate-500 mb-6" data-testid="insights-count">
          {loading ? "Chargement…" : `${total} publication${total > 1 ? "s" : ""}`}
        </div>

        {!loading && items.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-600">
            Aucune publication ne correspond à votre recherche.
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(a => (
            <Link
              key={a.id}
              to={`/insights/${a.slug}`}
              data-testid={`insight-card-${a.slug}`}
              className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:border-[#C9A961]/40 hover:shadow-md transition-smooth flex flex-col"
            >
              {a.cover_image && (
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  <img src={a.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-smooth" />
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(a.profession_tags || []).slice(0, 2).map(t => (
                    <Badge key={t} className="bg-[#C9A961]/10 text-[#8A6E3A] border border-[#C9A961]/20 rounded-full text-[10px] px-2 hover:bg-[#C9A961]/15">
                      {PROFESSION_LABELS[t] || t}
                    </Badge>
                  ))}
                </div>
                <h2 className="font-serif text-xl text-[#0B3A2A] leading-snug mb-3 group-hover:text-[#0d1729] transition-smooth">
                  {a.title}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed mb-5 line-clamp-3 flex-1">
                  {a.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> {a.reading_time || 1} min de lecture
                  </span>
                  <span className="flex items-center gap-1 text-[#C9A961] group-hover:translate-x-0.5 transition-smooth">
                    Lire <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-white rounded-2xl border border-slate-100 p-8 lg:p-10 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-3">Vous êtes spécialiste référencé ?</div>
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-[#0B3A2A] mb-3">
            Publiez vos analyses sur CONFILEX
          </h3>
          <p className="text-slate-600 max-w-2xl mx-auto mb-6">
            Les contenus sont relus par le comité éditorial avant publication. Aucun référencement payant — la qualité éditoriale prime.
          </p>
          <Button asChild className="rounded-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white">
            <Link to="/expert/articles/new">Soumettre un article</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ProfChip({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-smooth border ${
        active
          ? "bg-[#0B3A2A] text-white border-[#0B3A2A]"
          : "bg-white text-slate-700 border-slate-200 hover:border-[#C9A961]/40 hover:text-[#0B3A2A]"
      }`}
    >
      {label}
    </button>
  );
}
